// src/pages/PromptStatus1.js - Completely rewritten based on PromptStatus3
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar4 from '../components/Navbar4';
import { getFirstPromptStatus } from '../services/LLMCalls';
import ProcessWarning from '../components/ProcessWarning';
import ProcessingTime from '../components/ProcessingTime';

const PromptStatus1 = () => {
  // Get location data
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId, promptId, isProcessing, error: initialError } = location.state || {};
  
  // Component state
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(initialError || null);
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  
  // Use refs for stable values
  const startTimestampRef = useRef(Date.now());
  const attemptsRef = useRef(0);
  const isMountedRef = useRef(true);
  const statusRef = useRef(status);  // Add this line
  
  // Update statusRef whenever status changes
  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  
  // Constants
  const MAX_ATTEMPTS = 30;
  
  // Navigation handler
  const handleNavigate = useCallback((to, options) => {
    if (status === 'processing') {
      setShowModal(true);
      setPendingNavigation(() => () => navigate(to, options));
    } else {
      navigate(to, options);
    }
  }, [status, navigate]);
  
  // Back button handler
  const handleBack = useCallback(() => {
    handleNavigate('/create-view', { state: { projectId } });
  }, [handleNavigate, projectId]);
  
  // Modal handlers
  const handleModalCancel = useCallback(() => {
    setShowModal(false);
    setPendingNavigation(null);
  }, []);
  
  const handleModalContinue = useCallback(() => {
    setShowModal(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }, [pendingNavigation]);

  // Prevent page unload
  useEffect(() => {
    if (status !== 'processing') return;
    
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [status]);
  
  // Main polling logic
  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true;
    
    // Initial validation
    if (!projectId) {
      setError('Missing project ID');
      return;
    }
    
    if (!promptId && !isProcessing) {
      setError('Invalid status check request');
      return;
    }
    
    console.log(`[PromptStatus1] Initializing polling for promptId: ${promptId}`);
    
    // Poll status function
    const pollStatus = async () => {
      if (!isMountedRef.current) return;
      
      try {
        if (!promptId) return;
        
        console.log(`[PromptStatus1] Checking status for promptId: ${promptId}`);
        const result = await getFirstPromptStatus(promptId);
        
        // Skip if component unmounted during API call
        if (!isMountedRef.current) return;
        
        console.log(`[PromptStatus1] Status received:`, result);
        
        // Update state based on response
        if (result.step1_status === 'error') {
          setError(result.error_message || 'An error occurred during processing.');
          setStatus('error');
        } 
        else if (result.step1_status === 'complete') {
          console.log(`[PromptStatus1] Status complete, navigating...`);
          
          // Skip final status update and navigate directly
          // This is the key difference that prevents the UI flicker
          navigate('/clarifications', {
            state: { projectId, promptId },
            replace: true
          });
          
          // Stop polling
          return false;
        }
        else if (result.step1_status !== statusRef.current) {  // Use statusRef instead
          setStatus(result.step1_status);
        }
        
        // Continue polling
        return true;
      } catch (err) {
        console.error(`[PromptStatus1] Error checking status:`, err);
        
        // Skip if component unmounted during error handling
        if (!isMountedRef.current) return false;
        
        // Track attempts and set error if exceeded
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setError('Request timed out. Please try again.');
          return false;
        }
        
        // Continue polling on error
        return true;
      }
    };
    
    // Start polling
    let pollId;
    const doPoll = () => {
      pollStatus().then(shouldContinue => {
        if (shouldContinue && isMountedRef.current) {
          pollId = setTimeout(doPoll, 2000);
        }
      });
    };
    
    // Do initial poll
    doPoll();
    
    // Cleanup
    return () => {
      console.log(`[PromptStatus1] Unmounting, clearing polling`);
      isMountedRef.current = false;
      clearTimeout(pollId);
    };
  }, [projectId, promptId, isProcessing, navigate, MAX_ATTEMPTS]);
  
  // Render UI components
  const ErrorDisplay = () => (
    <div className="text-center p-4">
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
      <button
        onClick={() => navigate('/create-view')}
        className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
      >
        Back to Create View
      </button>
    </div>
  );

  const LoadingDisplay = () => (
    <div className="text-center p-4">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900 mx-auto mb-4"></div>
      <p className="text-xl mb-2">
        {status === 'processing' && 'Processing your request...'}
        {status === 'analyzing' && 'Analyzing your instructions...'}
        {status === 'generating' && 'Generating clarification questions...'}
      </p>
      <p className="text-lg text-gray-600">This may take a few moments</p>
      
      {/* Only render ProcessingTime once and with stable timestamp */}
      <ProcessingTime 
        key="processing-time" // Force single instance
        promptType="step1" 
        status={status} 
        startTimestamp={startTimestampRef.current} 
      />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar4
        title="Processing"
        backPath="/create-view"
        projectId={projectId}
        onBack={handleBack}
      />
      <div className="flex-grow flex items-center justify-center">
        {error ? <ErrorDisplay /> : <LoadingDisplay />}
      </div>
      <ProcessWarning
        open={showModal}
        onCancel={handleModalCancel}
        onContinue={handleModalContinue}
      />
    </div>
  );
};

export default PromptStatus1;